import { useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { api } from "@/lib/api";
import { getOfflineAudio } from "@/lib/offline";
import { track } from "@/lib/track";

const cache = new Map();

function speakFallback(text, rate = 1) {
  return new Promise((resolve, reject) => {
    if (!window.speechSynthesis) return reject(new Error("no tts"));
    const u = new SpeechSynthesisUtterance(text.replace(/[（）()～〜…]/g, ""));
    u.lang = "ja-JP"; u.rate = 0.85 * rate;
    u.onend = resolve; u.onerror = reject;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  });
}

export function SpeakButton({ text, size = "sm", testid = "speak-button", className = "", rate = 1 }) {
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stop = () => { audioRef.current?.pause(); window.speechSynthesis?.cancel(); setState("idle"); };

  const play = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (state === "playing") return stop();
    setState("loading");
    try {
      let url = cache.get(text);
      if (!url) {
        let blob = null;
        try { blob = (await api.get("/tts", { params: { text }, responseType: "blob" })).data; } catch (e) { blob = await getOfflineAudio(text); if (!blob) throw e; }
        url = URL.createObjectURL(blob); cache.set(text, url);
      }
      const a = new Audio(url);
      track("tts_play", { len: text.length });
      a.playbackRate = rate;
      a.preservesPitch = true;
      audioRef.current = a;
      a.onended = () => setState("idle");
      a.onerror = () => setState("idle");
      setState("playing");
      await a.play();
    } catch {
      try { setState("playing"); await speakFallback(text, rate); } catch (e) { console.warn("TTS fallback unavailable", e?.message); setState("idle"); }
      setState("idle");
    }
  };

  const dim = size === "sm" ? "h-9 w-9 md:h-7 md:w-7" : "h-10 w-10 md:h-9 md:w-9";
  const icon = size === "sm" ? "h-4 w-4 md:h-3.5 md:w-3.5" : "h-4 w-4";
  return (
    <button type="button" data-testid={testid} data-state={state} onClick={play} aria-label="Putar suara" title="Putar suara"
      className={`inline-flex ${dim} shrink-0 items-center justify-center rounded-full border border-border bg-card text-secondary transition-[background-color,transform] hover:bg-secondary/10 active:scale-95 ${state === "playing" ? "border-secondary bg-secondary/10" : ""} ${className}`}>
      {state === "loading" ? <Loader2 className={`${icon} animate-spin`} /> : state === "playing" ? <Square className={icon} /> : <Volume2 className={icon} />}
    </button>
  );
}
